import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { getAffiliatePageStyles } from './affiliateStyles';
import NumericInput from '../../components/NumericInput';
import './affiliates.css';

export default function AddAffiliate() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    code: '',
    commissionRate: '',
    discountType: 'percentage',
    discountValue: '',
    status: 'active',
    notes: '',
  });

  const styles = getAffiliatePageStyles(isMobile, isSmallMobile);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.commissionRate === '') {
      toast.error('Commission % is required');
      return;
    }
    if (form.discountType !== 'none' && form.discountValue === '') {
      toast.error('Discount value is required');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/v1/affiliate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          commissionRate: Number(form.commissionRate),
          discountValue: Number(form.discountValue),
        }),
      });
      const result = await parseJsonResponse(response);
      toast.success('Affiliate created successfully');
      navigate('/affiliates/list');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to create affiliate. Please try again later.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.mainWrapper}>
      <Toaster position="top-center" />
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div>
            <h1 style={styles.headerTitle}>Add Affiliate</h1>
            <p style={styles.breadcrumbs}>Home • Affiliates • Add New</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.formSection} autoComplete="off">
          {[
            ['name', 'Full Name', 'text', 'affiliate-name'],
            ['email', 'Email', 'text', 'affiliate-contact-email'],
            ['phone', 'Phone', 'tel', 'affiliate-phone'],
            ['code', 'Affiliate Code', 'text', 'affiliate-code'],
          ].map(([field, label, type, inputName]) => (
            <div key={field} style={styles.formGroup}>
              <label style={styles.formLabel}>{label}</label>
              <input
                type={type}
                name={inputName}
                className="affiliate-form-input"
                value={form[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                required={field !== 'phone'}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-lpignore="true"
                data-form-type="other"
                inputMode={field === 'email' ? 'email' : undefined}
              />
            </div>
          ))}

          <div style={styles.formGrid2}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Commission %</label>
              <NumericInput
                min="0"
                max="100"
                name="affiliate-commission"
                className="affiliate-form-input"
                placeholder="Commission %"
                value={form.commissionRate}
                onChange={(e) => handleChange('commissionRate', e.target.value)}
                autoComplete="off"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Status</label>
              <select
                name="affiliate-status"
                className="affiliate-form-input"
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>

          <div style={styles.formGrid2}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Discount Type</label>
              <select
                name="affiliate-discount-type"
                className="affiliate-form-input"
                value={form.discountType}
                onChange={(e) => handleChange('discountType', e.target.value)}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
                <option value="none">No Discount (link only)</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Discount Value</label>
              <NumericInput
                min="0"
                name="affiliate-discount-value"
                className="affiliate-form-input"
                placeholder="Discount value"
                value={form.discountValue}
                onChange={(e) => handleChange('discountValue', e.target.value)}
                disabled={form.discountType === 'none'}
                autoComplete="off"
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Notes</label>
            <textarea
              name="affiliate-notes"
              className="affiliate-form-input"
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              autoComplete="off"
            />
          </div>

          <button
            type="submit"
            disabled={
              loading ||
              form.commissionRate === '' ||
              (form.discountType !== 'none' && form.discountValue === '')
            }
            style={{
              ...styles.primaryButton,
              opacity:
                loading ||
                form.commissionRate === '' ||
                (form.discountType !== 'none' && form.discountValue === '')
                  ? 0.7
                  : 1,
              cursor:
                loading ||
                form.commissionRate === '' ||
                (form.discountType !== 'none' && form.discountValue === '')
                  ? 'not-allowed'
                  : 'pointer',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            {loading ? 'Creating...' : 'Create Affiliate'}
          </button>
        </form>
      </div>
    </div>
  );
}
